import { DesignOrigin, PdfPage, SealingServiceComponentDesign } from '@paragon/contract-library';
import { designService, sealingService } from '@paragon/data-connections';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { clearActiveLayerPaperJs } from '../modules/annotations/paperTranslator';
import { proceedOnlyIfJobIsUnlocked } from '../modules/jobLocking';
import { getDocuments, getSelectedDocument, getSelectedPage, getSortedDocuments } from '../selectors/documents';
import {
  designServiceComponentIdLoaded,
  documentNotesModified,
  documentNotesSaved,
  documentPageCreated,
  documentPageDeleted,
  documentPageSelected,
  documentRemoved,
  documentReplaced,
  documentSelected,
  documentsLoaded,
  pagesFlyoutClosed,
  pagesFlyoutOpened,
  SanitizedDocument,
} from '../store/documents';
import { jobSetDesignOrigin } from '../store/job';
import { loadAnnotationsForPage, savePageAnnotations } from './annotations';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ReduxDispatch, ThunkActionVoid } from './thunkActions';

export function loadDocuments(jobId: number): AsyncThunkActionVoid {
  return (dispatch) =>
    sealingService.jobs.getDocumentsForJob(jobId).then((documents) => {
      // assume job will have same design origin for all files, see discussion on link below
      // https://trello.com/c/aBSBcmpl/2997-automatically-choose-stamping-template-applied-by-sealjob-button-based-on-what-kind-of-job-is-being-sealed
      let designOrigin: DesignOrigin = 'Mitek';
      if (documents && documents.length > 0) {
        const referenceDocument = documents.find((document) => document.designOrigin);
        if (referenceDocument) {
          ({ designOrigin } = referenceDocument);
        }
        dispatch(jobSetDesignOrigin({ designOrigin }));
      }
      dispatch(documentsLoaded({ documents }));
      dispatch(selectFirstDocument());
    });
}

export function selectDocument(documentId: number): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(saveModifiedNotes());
    dispatch(savePageAnnotations());
    dispatch(loadDesignServiceComponentId(documentId));

    dispatch(documentSelected({ documentId }));

    dispatch(loadAnnotationsForPage(state.documents.pdfData[documentId].pdfPages[0].id));
  };
}

export function selectFirstDocument(): ThunkActionVoid {
  return (dispatch, getState) => {
    const documents = getSortedDocuments(getState());

    if (documents.length === 0) {
      return;
    }

    dispatch(selectDocument(documents[0].id));
  };
}

export function selectLastDocument(): ThunkActionVoid {
  return (dispatch, getState) => {
    const documents = getSortedDocuments(getState());

    if (documents.length === 0) {
      return;
    }

    dispatch(selectDocument(documents[documents.length - 1].id));
  };
}

export function selectDocumentAtIndex(index: number): ThunkActionVoid {
  return (dispatch, getState) => {
    const documents = getDocuments(getState());

    if (documents.length === 0) {
      return;
    }

    const documentAtIndex = documents.find((document) => document.index === index);
    if (documentAtIndex === undefined) {
      return;
    }

    dispatch(selectDocument(documentAtIndex.id));
  };
}

export function selectPage(pageNumber: number): ThunkActionVoid {
  return (dispatch, getState) => {
    dispatch(savePageAnnotations());

    dispatch(documentPageSelected({ pageNumber }));

    const activePage = getSelectedPage(getState())!;
    dispatch(loadAnnotationsForPage(activePage.id));
  };
}

export function selectNextPage(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageCount = getSelectedDocument(state).pdfPages.length;
    const currentPageNumber = state.documents.activePageNumber;
    let newPageNumber = currentPageNumber + 1;

    if (newPageNumber === pageCount) {
      newPageNumber = 0;
    }

    dispatch(selectPage(newPageNumber));
  };
}

export function selectPreviousPage(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageCount = getSelectedDocument(state).pdfPages.length;
    let currentPageNumber = getState().documents.activePageNumber;

    if (currentPageNumber === 0) {
      currentPageNumber = pageCount;
    }

    dispatch(selectPage(currentPageNumber - 1));
  };
}

// TODO: move these to a user interface action creator?
export function showPagesFlyout(documentId: number) {
  return pagesFlyoutOpened({ documentId });
}

export function hidePagesFlyout() {
  return pagesFlyoutClosed();
}

export function selectPreviousDocument(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const { activeDocumentId } = state.documents;

    let newIndex: number;
    if (activeDocumentId == null) {
      newIndex = state.documents.pdfCount - 1;
    } else {
      newIndex = state.documents.pdfData[activeDocumentId].index - 1;
    }
    if (newIndex < 0) {
      newIndex = state.documents.pdfCount - 1;
    }

    const documents = Object.values(state.documents.pdfData);
    const documentId = documents.find((pdf) => pdf.index === newIndex)!.id;

    dispatch(selectDocument(documentId));
  };
}

export function selectNextDocument(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const { activeDocumentId } = state.documents;

    let newIndex: number;
    if (activeDocumentId == null) {
      newIndex = 0;
    } else {
      newIndex = state.documents.pdfData[activeDocumentId].index + 1;
    }
    if (newIndex > state.documents.pdfCount - 1) {
      newIndex = 0;
    }

    const documents = Object.values(state.documents.pdfData);
    const documentId = documents.find((pdf) => pdf.index === newIndex)!.id;

    dispatch(selectDocument(documentId));
  };
}

export function updateNotes(documentId: number, newNotes: string) {
  return documentNotesModified({ documentId, newNotes });
}

export function saveModifiedNotes(): AsyncThunkActionReturn<void[]> {
  return (dispatch, getState) => {
    const state = getState();

    const savingPromises = [];

    for (const document of Object.values(state.documents.pdfData)) {
      if (!document.notesModified) {
        continue;
      }

      const spinnerId = dispatch(addSpinnerMessage('Saving notes...'));

      savingPromises.push(
        sealingService.pdfs
          .saveNotes(document.id, document.notes)
          .then(() => {
            dispatch(documentNotesSaved());
          })
          .catch(() => dispatch(enqueueWarning(`Warning: unable to save notes for ${document.name}`)))
          .then(() => {
            dispatch(removeSpinnerMessage(spinnerId));
          }),
      );
    }

    return Promise.all(savingPromises);
  };
}

export function deleteDocument(documentId: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const state = getState();

    const spinnerId = dispatch(addSpinnerMessage('Deleting document...'));

    // TODO: this should be handled somewhere else
    const document = state.documents.pdfData[documentId];
    if (document.layer != null) {
      document.layer.remove();
    }

    try {
      if (document.componentDesignId != null) {
        await sealingService.componentDesigns.remove(document.componentDesignId);
      } else {
        await sealingService.pdfs.remove(documentId);
      }

      const pageIds = document.pdfPages.map((p) => p.id);
      dispatch(documentRemoved({ documentId, pageIds }));
    } catch (error) {
      console.error(error);
    }
    dispatch(removeSpinnerMessage(spinnerId));

    if (getState().documents.pdfCount === 0) clearActiveLayerPaperJs();
  };
}

export function createPage(documentId: number, newPageNumber: number): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const state = getState();

    if (state.documents.activeDocumentId == null) {
      // TODO: how to handle if no document is selected?  create a new document?
    }

    dispatch(savePageAnnotations());
    const spinnerId = dispatch(addSpinnerMessage('Adding page...'));

    return sealingService.pdfPages
      .createBlankPage(state.documents.activeDocumentId!, newPageNumber)
      .then((createdPage) => {
        dispatch(documentPageCreated({ documentId, createdPage }));
        dispatch(selectPage(newPageNumber));
      })
      .catch((err) => console.error(err))
      .then(() => {
        dispatch(removeSpinnerMessage(spinnerId));
      });
  };
}

export function deletePageByNumber(documentId: number, pageNumber: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const state = getState();
    const document = state.documents.pdfData[documentId];

    // Don't allow the only page to be deleted
    if (document.pdfPages.length <= 1) {
      return Promise.resolve();
    }

    const pageToDelete = document.pdfPages.find((page) => page.pageNumber === pageNumber)!;

    return dispatch(actuallyDeletePage(pageToDelete.id));
  };
}

/**
 * Deletes all pages in the given set of page IDs.  If all the pages of a document are included,
 *   the document itself will be deleted instead.
 * @param selectedPages {ReadonlyArray<number>}
 */
export function deletePages(selectedPages: readonly number[]): AsyncThunkActionVoid {
  const isMarkedForDeletion = (page: PdfPage) => selectedPages.includes(page.id);

  return async (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const documents = getDocuments(getState());
    const dispatcher = createSynchronousDispatcher(dispatch);

    // First locate any documents whose pages are all marked for deletion, as that means the
    //   document itself should be deleted.
    const documentsToDelete = documents.filter((d) => d.pdfPages.every(isMarkedForDeletion));
    if (documentsToDelete.length < documents.length) {
      dispatcher.run(
        deleteDocument,
        documentsToDelete.map((d) => d.id),
      );
    } else {
      dispatch(enqueueWarning("Can't delete every document in a job."));
      return;
    }

    const pagesInDeletedDocuments = new Set(
      documentsToDelete
        .reduce<PdfPage[]>((pages, document) => pages.concat(document.pdfPages), [])
        .map((page: PdfPage) => page.id),
    );

    // Any pages not in a deleted document must be deleted separately.
    const pagesToDelete = selectedPages.filter((pageId) => !pagesInDeletedDocuments.has(pageId));
    dispatcher.run(actuallyDeletePage, pagesToDelete);

    await dispatcher.whenDone();
  };
}

function actuallyDeletePage(pageId: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Deleting page...'));

    try {
      const pageToDelete = findPage(getDocuments(getState()), pageId)!;
      await sealingService.pdfPages.remove(pageToDelete.id);

      dispatch(
        documentPageDeleted({
          documentId: pageToDelete.pdfId,
          pageId: pageToDelete.id,
        }),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function regenerateTdds(selectedPages: readonly number[]): AsyncThunkActionVoid {
  const isMarkedForRegeneration = (page: PdfPage) => selectedPages.includes(page.id);

  return async (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const documents = getDocuments(getState());
    const dispatcher = createSynchronousDispatcher(dispatch);
    const hasComponentDesign = (document: SanitizedDocument) => document.componentDesignId !== null;
    const selectedDocuments = documents.filter((d) => d.pdfPages.every(isMarkedForRegeneration));
    if (selectedDocuments.some((d) => !hasComponentDesign(d))) {
      dispatch(enqueueError('You cannot generate TDDs from single PDFs.'));
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Loading design information...'));

    let componentDesigns: readonly SealingServiceComponentDesign[];
    try {
      componentDesigns = await Promise.all(
        selectedDocuments.map((d) => sealingService.componentDesigns.get(d.componentDesignId)),
      );
    } catch (error) {
      dispatch(enqueueError('Error loading design information.'));
      console.error(error);
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    if (componentDesigns.some((c) => c.designOrigin !== 'Paragon')) {
      dispatch(enqueueError('Paragon TDDs are only regenerated for Paragon designs. Stopping.'));
      return;
    }

    dispatcher.run(
      regenerateTdd,
      selectedDocuments.map((document) => ({
        document,
        componentDesign: componentDesigns.find((c) => c.id === document.componentDesignId)!,
      })),
    );

    await dispatcher.whenDone();
  };
}

/**
 * Regenerate the TDD by creating a new one and deleting the old one.
 */
function regenerateTdd({
  document,
  componentDesign,
}: {
  readonly document: SanitizedDocument;
  readonly componentDesign: SealingServiceComponentDesign;
}): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage(`Regenerating TDD for ${document.name}...`));

    function enqueueUserErrorMessage(userVisibleExceptionMessage?: string) {
      dispatch(
        enqueueError(
          `Error regenerating TDD for ${document.name}${
            userVisibleExceptionMessage === undefined ? '' : `: ${userVisibleExceptionMessage}`
          }.`,
        ),
      );
    }

    try {
      const analyzeByComponentDesignGuidResult = await designService.componentDesigns.postAnalyzeByComponentDesignGuid(
        componentDesign.analysisRemoteKey!,
        { commitName: 'Regenerating TDD', abbreviated: true, logVariables: false },
      );
      if (analyzeByComponentDesignGuidResult.result === 'Exception') {
        enqueueUserErrorMessage(
          analyzeByComponentDesignGuidResult.isUserVisibleException
            ? analyzeByComponentDesignGuidResult.exception
            : undefined,
        );
        console.error(analyzeByComponentDesignGuidResult.exception);
        return;
      }
      const addedComponentDesign = await designService.componentDesigns.postCreateTddInJob(
        analyzeByComponentDesignGuidResult.analysisReportGuid,
        getState().job.id,
        componentDesign.id,
      );
      const newDocument = await sealingService.jobs.getDocument(addedComponentDesign.pdfFileId!);

      dispatch(
        documentReplaced({
          oldDocumentId: document.id,
          newDocument,
        }),
      );
      const loadAnnotationsPromise =
        newDocument.id === getState().documents.activeDocumentId
          ? dispatch(loadAnnotationsForPage(newDocument.pdfPages[0].id))
          : Promise.resolve();
      const loadDesignServiceComponentIdPromise = dispatch(loadDesignServiceComponentId(newDocument.id));
      await Promise.all([loadAnnotationsPromise, loadDesignServiceComponentIdPromise]);
    } catch (error) {
      enqueueUserErrorMessage();
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function loadDesignServiceComponentId(documentId: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();

    const pdf = state.documents.pdfData[documentId];
    if (pdf.designServiceComponentId != null) return;
    if (pdf.componentDesignId == null) return;

    const componentDesign = await sealingService.componentDesigns.get(pdf.componentDesignId);
    dispatch(
      designServiceComponentIdLoaded({
        documentId,
        designServiceComponentId: componentDesign.analysisRemoteKey,
      }),
    );
  };
}

function findPage(documents: readonly SanitizedDocument[], pageId: number) {
  for (const document of documents) {
    for (const page of document.pdfPages) {
      if (page.id === pageId) {
        return page;
      }
    }
  }

  return null;
}

/**
 * Utility to apply actions to items in multiple arrays and await them all with a single call.
 */
function createSynchronousDispatcher(dispatch: ReduxDispatch) {
  const queuedTasks: (() => Promise<void>)[] = [];

  return {
    run<T>(action: (item: T) => AsyncThunkActionVoid, items: readonly T[]) {
      queuedTasks.push(...items.map((item) => () => dispatch(action(item))));
    },

    async whenDone() {
      for (const task of queuedTasks) {
        await task();
      }
    },
  };
}
